import { isAfter } from 'date-fns';
import {
  Category, EditorListingCategory, SubCategory,
} from 'src/common/types/api/listings';
import { GeocoderResult } from 'src/common/components/Search/typesGoogle';
import captureException from 'src/common/utils/captureException';
import { DepartureLocationData, StreetAddressData } from './components/boat-location/boatLocationTypes';
import { AddressDetails } from './utils/getLocationData';
import { ListingFormState } from './types';
import { COUNTRY_USA } from './constants';

export const mapCategoryFields = (
  categories: Category[],
  boatCategories: EditorListingCategory[] = [],
): {
  primaryCategory?: Category;
  primarySubcategory?: SubCategory;
  secondCategory?: Category;
  secondSubcategory?: SubCategory;
  thirdCategory?: Category;
  thirdSubcategory?: SubCategory;

} => {
  const categoryData = {
    primaryCategory: null,
    primarySubcategory: null,
    secondCategory: null,
    secondSubcategory: null,
    thirdCategory: null,
    thirdSubcategory: null,
  };
  if (!categories || !boatCategories) return null;

  // Get Categories data
  boatCategories.forEach((boatCategory) => {
    const category = categories.find((cat) => cat.id === boatCategory.id);
    if (category) {
      if (boatCategory.primary) {
        categoryData.primaryCategory = category;
      } else if (!categoryData.secondCategory) {
        categoryData.secondCategory = category;
      } else {
        categoryData.thirdCategory = category;
      }
    }
  });

  boatCategories.forEach((boatCategory) => {
    const hasCategory = categories.some((cat) => cat.id === boatCategory.id);
    if (!hasCategory) {
      // finding a primary subcategory
      if (categoryData.primaryCategory && !categoryData.primarySubcategory) {
        const subCategory = categoryData.primaryCategory.subcategories.find(
          ({ id }) => id === boatCategory.id,
        );
        if (subCategory) {
          categoryData.primarySubcategory = subCategory;
        }
      }
      // finding a secondary subcategory
      if (categoryData.secondCategory && !categoryData.secondSubcategory) {
        const subCategory = categoryData.secondCategory.subcategories.find(
          ({ id }) => id === boatCategory.id,
        );
        if (subCategory) {
          categoryData.secondSubcategory = subCategory;
        }
      }
      // finding a tertiary subcategory
      if (categoryData.thirdCategory && !categoryData.thirdSubcategory) {
        const subCategory = categoryData.thirdCategory.subcategories.find(
          ({ id }) => id === boatCategory.id,
        );
        if (subCategory) {
          categoryData.thirdSubcategory = subCategory;
        }
      }
    }
  });
  return categoryData;
};

export const updateForm = <T>(
  data: Partial<T>,
  formValueChangeFn: (key: string, value) => void,
): void => {
  Object.keys(data).forEach((key: string) => formValueChangeFn(key, data[key]));
};

export const isNumber = (x: unknown): x is number => (
  typeof x === 'number' && !Number.isNaN(x)
);

export const shouldSubmitListingForReview = (
  active: boolean,
  /** e.g. `"2020-12-10T14:30:07Z"` */
  dateModified: string,
  /** e.g. `"2020-12-10T14:30:07Z"` */
  datePublished: string | null,
): boolean => {
  //  No need to review if listing is published
  if (active) {
    return false;
  }
  /**
   * If datePublished is null then it should be considered as reviewable
   * OR
   * If dateModified is greater than datePublished then it should be
   * considered as reviewable
   */
  if (!datePublished || isAfter(new Date(dateModified), new Date(datePublished))) {
    return true;
  }
  /**
   * Otherwise it should not be considered as reviewable. This also handles
   * when dateModified is less than or equal to datePublished
   */
  return false;
};

function streetAddressFromPlaceDetails(placeDetails: AddressDetails): string {
  if (!placeDetails.address && !placeDetails.addressText) {
    captureException(new Error('Place details missing address and address text'), { extra: { placeDetails } });
  }
  return placeDetails.address || placeDetails.addressText || '';
}

export const mapPlaceDetailsToStreetAddress = (
  placeDetails: AddressDetails,
): Omit<StreetAddressData, 'title'> => ({
  street_address: streetAddressFromPlaceDetails(placeDetails),
  street_city: placeDetails.city,
  street_state: placeDetails.state,
  street_country: placeDetails.country,
  street_zip: placeDetails.zip,
  street_latitude: placeDetails.lat,
  street_longitude: placeDetails.long,
  autocomplete_text: placeDetails.addressText || placeDetails.address,
  is_valid_street_address: placeDetails.isValidAddress,
});

export const mapPlaceDetailsToDepartureLocation = (
  placeDetails: AddressDetails,
): DepartureLocationData => ({
  departure_address: streetAddressFromPlaceDetails(placeDetails),
  departure_address_text: placeDetails.addressText,
  departure_city: placeDetails.city,
  departure_state: placeDetails.state,
  departure_country: placeDetails.country,
  departure_zip: placeDetails.zip,
  departure_latitude: placeDetails.lat,
  departure_longitude: placeDetails.long,
  same_address: false,
});

/**
 * Validate street address details
 * If the country is United States, validate the street address details
 * Otherwise, validate the latitude, longitude, and country.
 * This special check is needed for USA to handle taxes.
 * @param values - Street address data
 * @returns True if the street address details are valid, false otherwise
 */
export const validateStreetAddressDetails = (values: StreetAddressData): boolean => {
  const country = values.street_country;
  if (country === COUNTRY_USA) {
    return !!(
      values.street_address
      && values.street_city
      && values.street_state
      && values.street_country
      && values.street_zip
      && values.street_latitude
      && values.street_longitude);
  }
  return !!(
    values.street_latitude
    && values.street_longitude
    && values.street_country
  );
};

export const hasBoatLocationFromListing = (values: ListingFormState): boolean => {
  const boatLocation = values.boatLocations?.[0];
  return !!(
    (
      boatLocation?.street_latitude
    && boatLocation?.street_longitude
    )
    || (
      boatLocation?.departure_latitude
    && boatLocation?.departure_longitude
    )
    || boatLocation?.arrival_instructions

  );
};

export type LocationDetails = {
  title?: string;
  address: string;
  city: string;
  country: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
};

/**
 * Validate address warning
 * If the country is United States, validate the  address warning
 * Otherwise, validate the latitude, longitude, and country.
 * This special check is needed for USA to handle taxes.
 * @param location
 * @returns
 */
export const hasAddressWarning = (
  location: LocationDetails,
  isStreetAddress: boolean,
): boolean => {
  const country = location.country;
  const validateTitle = isStreetAddress ? !location.title : false;
  /**
   * If the address is street address and the country is United States,
   * then validation is strict. Otherwise, validation is lenient.
   */
  if (isStreetAddress && country === COUNTRY_USA) {
    return (
      validateTitle
      || !location.address
      || !location.city
      || !location.state
      || !location.zip
      || !location.latitude
      || !location.longitude
      || !location.country
    );
  }
  return (
    validateTitle
    || !location.latitude
    || !location.longitude
    || !location.country
  );
};

export const isInView = (el: HTMLElement, parentEl: HTMLElement): boolean => {
  if (!el || !parentEl) return false;
  const rect = el.getBoundingClientRect();
  const viewHeight = parentEl.clientHeight;
  const viewWidth = parentEl.clientWidth;

  const verticallyVisible = rect.top >= 0 && rect.bottom <= viewHeight;
  const horizontallyVisible = rect.left >= 0 && rect.right <= viewWidth;

  return verticallyVisible && horizontallyVisible;
};

/**
  * This method aims to filter out the results for better readable address for users.
  * First, it filters out the results to remove the plus code results.
  * Then, it finds the address with the street number.
  * If no address with the street number is found, it finds the address with the geometric center.
  * If no address with the geometric center is found, it returns the first result.
 * @param results
 * @returns NearestAddressResult
 */
export const getNearestAddressResult = (results: GeocoderResult[]): GeocoderResult => {
  // Filter out the results to remove the plus code results.
  const resultsWithoutPlusCode = results.filter((result) => !result.address_components.some((component) => component.types.includes('plus_code')));

  // Find the address with the street number.
  const addressWithStreetNumber = resultsWithoutPlusCode.find((result) => result.address_components.some((component) => component.types.includes('street_number')));
  let nearestAddress = addressWithStreetNumber || {} as GeocoderResult;

  // If no address with the street number is found, find the address with the geometric center.
  if (!addressWithStreetNumber) {
    const addressesWithGeometricCenter = resultsWithoutPlusCode.find((result) => result.geometry.location_type === 'GEOMETRIC_CENTER');

    // If an address with the geometric center is found, set it as the nearest address.
    nearestAddress = addressesWithGeometricCenter || resultsWithoutPlusCode[0];
  }

  // Return the nearest address.
  return nearestAddress;
};
